import React from 'react';
import LineGraph from '../../components/linegraph';
import PrintPageWrapper from '../../components/printpagewrapper';
import MuiDataGrid from '../../components/muidatagrid/MuiDataGrid';
import { createKpiColumns } from '../../components/muidatagrid/columnDefs/kpiColumns';
import { formatLoadDate, buildStoreHeaderInfo } from '../../utils/appUtils';
import { getTrendIndicator, getDateRange, getMonthYearRange } from '../../utils/performanceUtils';
import { KPI_DECIMAL_PLACES, KPI_PERCENTAGE_DECIMAL_PLACES } from '../../constants/constants';
import lang from '../../languages/en.json';
import styles from './PerformancePrintView.module.css';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setIsMultiStoreEnabled, selectIsMultiStoreEnabled } from '../../redux/UserReducer/userSlice';
import type { RootState } from '../../redux/store';
import type { UserDetails } from '../../models/UserModel';
import type { OverallMetrics, LineGraphModel } from '../../models/LineGraphModels';
import type { KpiGrid, KpiDetail } from '../../models/operationsmodels/KpiModels';
import type { PerformanceLLMGrid } from '../../models/PerformanceLLMGridModel';
import type { SalesPerformanceDetailsByFulfillment } from '../../models/SalesPerformanceDetailsByFulfillmentModel';
import type { ProductSyncDetails } from '../../models/ProductSyncDetailsModel';
import type { YardRXModel } from '../../models/YardRXModel';
import type { KeyOperationalRolling2Model } from '../../models/KeyOperationalRolling2Model';
import type { EcomOrderDetails } from '../../models/EcomOrderDetailsModel';

import PerformancePeerGroup, {
  CardData,
  createChangeValue,
  formatCurrentYear,
  formatRank,
  formatBenchmark,
} from './PerformancePeerGroup';

// Live components — rendered with data + isPrintView props
import Overall from './tabs/overall/Overall';
import SalesByFulfillment from './tabs/ecommerce/salesbyfulfillment/SalesByFulfillment';
import OrderDetailsComponent from './tabs/ecommerce/orderdetails/OrderDetails';
import KeyOperationalMetrics from './tabs/ecommerce/keyoperationalmetrics/KeyOperationalMetrics';
import ProductSyncDetailsComponent from './tabs/ecommerce/productsyncdetails/ProductSyncDetails';
import YardRx from './tabs/ecommerce/yardrx/YardRx';
import LocalLiftMax from './tabs/localliftmax/LocalLiftMax';

// ─── Constants ────────────────────────────────────────────────────────────────

const PRINT_CONTAINER_ID = 'performance-print-container';

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface WindowWithStore extends Window {
  __REDUX_STORE__?: {
    getState: () => RootState;
  };
}

interface ParentSnapshot {
  userDetails: UserDetails | null;
  isMultiStoreEnabled: boolean;
  overallData: OverallMetrics | null;
  salesByFulfillment: SalesPerformanceDetailsByFulfillment | null;
  productSync: ProductSyncDetails | null;
  yardRx: YardRXModel | null;
  ecommKpiGrids: KpiGrid[];
  orderDetails: EcomOrderDetails | null;
  keyOperationalMetrics: KeyOperationalRolling2Model | null;
  localLiftData: PerformanceLLMGrid | null;
}

// ─── Utility: read parent tab's Redux state ───────────────────────────────────

const getParentState = (): RootState | null => {
  try {
    const opener = window.opener as WindowWithStore | null;
    return opener?.__REDUX_STORE__?.getState() ?? null;
  } catch {
    return null;
  }
};

// ─── Sub-component: EcommKpiSection ──────────────────────────────────────────

interface EcommKpiSectionProps {
  kpiGrids: KpiGrid[];
}

const EcommKpiSection: React.FC<EcommKpiSectionProps> = ({ kpiGrids }) => {
  const isMultiStoreEnabled = useAppSelector(selectIsMultiStoreEnabled);
  if (kpiGrids.length === 0) {
    return null;
  }
  return (
    <>
      {kpiGrids.map((grid, idx) => (
        <div
          key={`ecomm-kpi-${idx}`}
          className={styles.ecommKpiSection}>
          <p className={styles.subsectionHeading}>{grid.title}</p>
          <p className={styles.sectionDate}>
            {lang.pageContent.datePrefix}{' '}
            {grid.loadDate ? formatLoadDate(grid.loadDate, false) : 'N/A'}
          </p>
          <div className={styles.gridWrapper}>
            <MuiDataGrid<KpiDetail, [boolean, boolean, string, string]>
              rows={grid.kpiDetails}
              getRowId={(row: KpiDetail) => row.kpiCode}
              createColumns={createKpiColumns}
              columnArgs={[true, isMultiStoreEnabled, grid.title, grid.cardName]}
              isPrintView={true}
              gridKey={`print-ecomm-kpi-${idx}`}
            />
          </div>
        </div>
      ))}
    </>
  );
};

// ─── Sub-component: GraphSection ────────────────────────────────────────────

interface GraphSectionProps {
  title: string;
  kpis: (LineGraphModel | null | undefined)[];
  dateRangeGetter?: (kpi: LineGraphModel) => string | undefined;
}

/**
 * Renders a titled grid of LineGraph cards for a given set of KPI codes.
 * Null/undefined entries are filtered out before rendering.
 */
const GraphSection: React.FC<GraphSectionProps> = ({ title, kpis, dateRangeGetter = getDateRange }): JSX.Element => (
  <>
    <p className={styles.graphSectionTitle}>{title}</p>
    <div className={styles.graphGrid}>
      {kpis
        .filter((kpi): kpi is LineGraphModel => Boolean(kpi))
        .map(kpi => (
          <div
            className={styles.graphItem}
            key={kpi.kpiCode}>
            <LineGraph
              title={kpi.title}
              mainValue={kpi.currentYtdAmount}
              format={kpi.format}
              decimalPlaces={KPI_DECIMAL_PLACES[kpi.kpiCode ?? '']}
              percentageDecimalPlaces={KPI_PERCENTAGE_DECIMAL_PLACES[kpi.kpiCode ?? '']}
              datasets={kpi.datasets}
              trendIndicator={getTrendIndicator(kpi)}
              dateRange={dateRangeGetter(kpi)}
            />
          </div>
        ))}
    </div>
  </>
);

// ─── Sub-component: OverallGraphs ────────────────────────────────────────────

interface OverallGraphsProps {
  overallData: OverallMetrics;
}

const OverallGraphs: React.FC<OverallGraphsProps> = ({ overallData }) => {
  const graphKpis = React.useMemo(
    () => ({
      TSALE: overallData.TSALE,
      SALES: overallData.SALES,
      ESALE: overallData.ESALE,
      SLSSF: overallData.SLSSF,
      GP: overallData.GP,
      EGP: overallData.EGP,
      GPSF: overallData.GPSF,
      GM: overallData.GM,
      EGM: overallData.EGM,
      ATS: overallData.ATS,
      TCNT: overallData.TCNT,
      ECNT: overallData.ECNT,
      TUNIT: overallData.TUNIT,
      IVALU: overallData.IVALU,
      RDI: overallData.RDI,
      RDR: overallData.RDR,
      RDO: overallData.RDO,
      B2S: overallData.B2S,
      B2P: overallData.B2P,
      B2T: overallData.B2T,
    }),
    [overallData],
  );

  return (
    <>
      <GraphSection
        title={lang.lineGraph.InStoreEcomHeader}
        dateRangeGetter={(kpi) => kpi.kpiCode === 'IVALU' ? getMonthYearRange(kpi) : getDateRange(kpi)}
        kpis={[
          graphKpis.TSALE,
          graphKpis.SALES,
          graphKpis.ESALE,
          graphKpis.SLSSF,
          graphKpis.GP,
          graphKpis.EGP,
          graphKpis.GPSF,
          graphKpis.GM,
          graphKpis.EGM,
          graphKpis.ATS,
          graphKpis.TCNT,
          graphKpis.ECNT,
          graphKpis.TUNIT,
          graphKpis.IVALU,
        ]}
      />
      <GraphSection
        title={lang.lineGraph.AceRewardsHeader}
        kpis={[graphKpis.RDI, graphKpis.RDR, graphKpis.RDO]}
      />
      <GraphSection
        title={lang.lineGraph.B2BHeader}
        kpis={[graphKpis.B2S, graphKpis.B2T, graphKpis.B2P]}
      />
    </>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * PerformancePrintView
 *
 * Standalone page rendered at /print-performance in a new browser tab.
 * Reads all Performance data from the parent tab's Redux store via
 * window.opener — no duplicate API calls.
 *
 * Renders: Performance Overview cards → Overall (table + graphs) →
 * Ecommerce (6 sections) → Local Lift — all in the same flat layout that
 * can be printed or saved as a PDF.
 */
const PerformancePrintView: React.FC = (): JSX.Element => {
  const [snapshot, setSnapshot] = React.useState<ParentSnapshot | null>(null);
  const [isParentUnavailable, setIsParentUnavailable] = React.useState<boolean>(false);
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    const parentState = getParentState();
    if (!parentState) {
      setIsParentUnavailable(true);
      return;
    }

    const { overall, ecommSalesPerf, productSync, yardRx, operations, user } = parentState;

    // Extract Ecommerce KPI grids — same filter as KeyPerformanceIndicators.tsx
    const ecommKpiGrids = (operations.kpiDashboard?.kpiGrids ?? []).filter(
      (g: KpiGrid) => g.cardName?.toLowerCase() === 'ecommerce',
    );

    const isMultiStore = user.isMultiStoreEnabled;
    // Sync the parent tab's multi-store flag into this print window's own Redux
    // store so components that read `selectIsMultiStoreEnabled` render correctly.
    dispatch(setIsMultiStoreEnabled(isMultiStore));

    const orderDetails: EcomOrderDetails | null = parentState.ecomOrderDetails?.data ?? null;

    const keyOpModel: KeyOperationalRolling2Model | null =
      parentState.keyOperationalMetrics?.data ?? null;

    const localLiftData: PerformanceLLMGrid | null = parentState.localLiftMax?.data ?? null;

    setSnapshot({
      userDetails: user.userDetails,
      isMultiStoreEnabled: isMultiStore,
      overallData: overall.overallPerformanceData,
      salesByFulfillment: ecommSalesPerf.salesByFulfillment,
      productSync: productSync.data,
      yardRx: yardRx.data,
      ecommKpiGrids,
      orderDetails,
      keyOperationalMetrics: keyOpModel,
      localLiftData,
    });
  }, [dispatch]);

  const reportDate = React.useMemo(
    () =>
      new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
    [],
  );

  const downloadFilename = React.useMemo(() => {
    const storeNumber = snapshot?.userDetails?.currentStoreNumber || 'unknown';
    const dateStr = new Date().toISOString().split('T')[0];
    return `Performance_Report_${storeNumber}_${dateStr}.pdf`;
  }, [snapshot?.userDetails?.currentStoreNumber]);

  const storeHeaderInfo = React.useMemo(
    () => buildStoreHeaderInfo(snapshot?.userDetails ?? null, snapshot?.isMultiStoreEnabled ?? false),
    [snapshot],
  );

  const cardData: CardData[] = React.useMemo(() => {
    if (!snapshot?.overallData) return [];
    const d = snapshot.overallData;
    return [
      {
        id: 1,
        title: lang.pageContent.sales,
        format: 'currency',
        rank: formatRank(d.TSALE?.ytdAmountRank),
        changePercent: createChangeValue(d.TSALE?.differencePercent),
        currentYear: formatCurrentYear(d.TSALE?.currentYtdAmount),
        benchmark: formatBenchmark(d.TSALE?.ytdBenchmarkAmount),
      },
      {
        id: 2,
        title: lang.pageContent.grossProfit,
        format: 'currency',
        rank: formatRank(d.TGP?.ytdAmountRank),
        changePercent: createChangeValue(d.TGP?.differencePercent),
        currentYear: formatCurrentYear(d.TGP?.currentYtdAmount),
        benchmark: formatBenchmark(d.TGP?.ytdBenchmarkAmount),
      },
      {
        id: 3,
        title: lang.pageContent.grossMargin,
        format: 'percent',
        rank: formatRank(d.TGM?.ytdAmountRank),
        changePercent: createChangeValue(d.TGM?.differencePercent),
        currentYear: formatCurrentYear(d.TGM?.currentYtdAmount),
        benchmark: formatBenchmark(d.TGM?.ytdBenchmarkAmount),
      },
      {
        id: 4,
        title: lang.pageContent.transactions,
        format: 'count',
        rank: formatRank(d.TTCNT?.ytdAmountRank),
        changePercent: createChangeValue(d.TTCNT?.differencePercent),
        currentYear: formatCurrentYear(d.TTCNT?.currentYtdAmount),
        benchmark: formatBenchmark(d.TTCNT?.ytdBenchmarkAmount),
      },
      {
        id: 5,
        title: lang.pageContent.avgTransaction,
        format: 'avgTransaction',
        rank: formatRank(d.TATS?.ytdAmountRank),
        changePercent: createChangeValue(d.TATS?.differencePercent),
        currentYear: formatCurrentYear(d.TATS?.currentYtdAmount),
        benchmark: formatBenchmark(d.TATS?.ytdBenchmarkAmount),
      },
    ];
  }, [snapshot?.overallData]);

  const handlePrint = React.useCallback(() => {
    window.print();
  }, []);

  React.useEffect(() => {
    const prev = document.title;
    document.title = downloadFilename.replace('.pdf', '');
    return () => {
      document.title = prev;
    };
  }, [downloadFilename]);

  const isContentReady = snapshot !== null;

  // ─── Error state + render ────────────────────────────────────────────────

  return (
    <PrintPageWrapper
      title={lang.performance.print.title}
      containerId={PRINT_CONTAINER_ID}
      isContentReady={isContentReady}
      isParentUnavailable={isParentUnavailable}
      onPrint={handlePrint}
      storeHeaderInfo={storeHeaderInfo}
      reportDate={reportDate}
      skeletonConfig={{ cardCount: 5, cardHeight: 200, sectionCount: 3 }}>
      {/* Performance Overview Cards */}
      <section
        className={styles.cardsSection}
        data-pdf-cards>
        <div className={styles.cardsGrid}>
          <PerformancePeerGroup cardData={cardData} isPrintView />
        </div>
        <div className={styles.cardsLegend}>
          <div className={styles.legendItem}>
            <span className={styles.legendDotBlue} />
            <span>{lang.pageContent.currentYear}</span>
          </div>
          {!snapshot?.isMultiStoreEnabled && (
            <div className={styles.legendItem}>
              <span className={styles.legendDotGray} />
              <span>{lang.pageContent.percentBenchmark}</span>
            </div>
          )}
        </div>
      </section>

      {snapshot && (
        <>
          {/* ── OVERALL (stacked: one grid per group for more room) ── */}
          <h1 className={styles.topSectionTitle}>{lang.overallPerformanceContent.overallLowerCase}</h1>
          <div
            className={styles.sectionBlock}
            data-pdf-section>
            <Overall
              data={snapshot.overallData}
              isPrintView
              visibleGroup="total"
            />
          </div>
          <div
            className={styles.sectionBlock}
            data-pdf-section>
            <Overall
              data={snapshot.overallData}
              isPrintView
              visibleGroup="store"
            />
          </div>
          <div
            className={styles.sectionBlock}
            data-pdf-section>
            <Overall
              data={snapshot.overallData}
              isPrintView
              visibleGroup="ecom"
            />
            {snapshot.overallData && <OverallGraphs overallData={snapshot.overallData} />}
          </div>

          {/* ── ECOMMERCE — Sales By Fulfillment (stacked: one grid per group) ── */}
          <h1 className={styles.topSectionTitle}>{lang.overallPerformanceContent.ecommerceLowerCase}</h1>
          <div
            className={styles.sectionBlock}
            data-pdf-section>
            <SalesByFulfillment
              data={snapshot.salesByFulfillment}
              isPrintView
              visibleGroup="sales"
            />
          </div>
          <div
            className={styles.sectionBlock}
            data-pdf-section>
            <SalesByFulfillment
              data={snapshot.salesByFulfillment}
              isPrintView
              visibleGroup="orders"
            />
          </div>
          <div
            className={styles.sectionBlock}
            data-pdf-section>
            <SalesByFulfillment
              data={snapshot.salesByFulfillment}
              isPrintView
              visibleGroup="units"
            />
          </div>
          <div
            className={styles.sectionBlock}
            data-pdf-section>
            <SalesByFulfillment
              data={snapshot.salesByFulfillment}
              isPrintView
              visibleGroup="grossProfit"
            />
          </div>

          {/* ── ECOMMERCE — Key Performance Indicators ─────────── */}
          <div
            className={styles.sectionBlock}
            data-pdf-section>
            <EcommKpiSection kpiGrids={snapshot.ecommKpiGrids} />
          </div>

          {/* ── ECOMMERCE — Order Details ─────────────────────── */}
          <div
            className={styles.sectionBlock}
            data-pdf-section>
            <OrderDetailsComponent
              data={snapshot.orderDetails}
              isPrintView
            />
          </div>

          {/* ── ECOMMERCE — Key Operational Metrics ───────────── */}
          <div
            className={styles.sectionBlock}
            data-pdf-section>
            <KeyOperationalMetrics
              data={snapshot.keyOperationalMetrics}
              isPrintView
            />
          </div>

          {/* ── ECOMMERCE — Product Sync Details ─────────────── */}
          <div
            className={styles.sectionBlock}
            data-pdf-section>
            <ProductSyncDetailsComponent
              data={snapshot.productSync}
              isPrintView
            />
          </div>

          {/* ── ECOMMERCE — Yard RX ───────────────────────────── */}
          <div
            className={styles.sectionBlock}
            data-pdf-section>
            <YardRx
              data={snapshot.yardRx}
              isPrintView
            />
          </div>

          {/* ── LOCAL LIFT ─────────────────────────────────────── */}
          <h1 className={styles.topSectionTitle}>{lang.overallPerformanceContent.localLiftLowerCase}</h1>
          <div
            className={styles.sectionBlock}
            data-pdf-section>
            <LocalLiftMax
              data={snapshot.localLiftData}
              isPrintView
            />
          </div>
        </>
      )}
    </PrintPageWrapper>
  );
};

export default PerformancePrintView;
